import { useState } from "react";
import { Controller, FormProvider, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { WFPButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const commentSchema = z.object({
  comment: z.string().min(1, "offerCommentForm.errorMessage"),
});

export type OfferCommentFormFields = z.infer<typeof commentSchema>;

interface OfferCommentFormProps {
  onValidate: (values: OfferCommentFormFields) => Promise<unknown>;
  methods: UseFormReturn<OfferCommentFormFields>;
}

export const OfferCommentForm = ({ onValidate, methods }: OfferCommentFormProps) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
  } = methods;
  const [isBusy, setBusy] = useState(false);

  const submit = (values: OfferCommentFormFields) => {
    setBusy(true);
    onValidate(values)
      .then(() => setValue("comment", ""))
      .catch(() => setError("comment", { message: "offerCommentForm.submitError" }))
      .finally(() => setBusy(false));
  };

  const errorMessage = errors.comment?.message;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submit)} className="flex w-full items-start gap-2 py-2">
        <Controller
          name="comment"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <div className="flex-1">
              <Input
                {...field}
                value={field.value ?? ""}
                id="comment"
                placeholder={t("offerCommentForm.commentPlaceholder")}
                aria-invalid={!!errorMessage}
              />
              {errorMessage && <p className="mt-1 text-danger-600 text-sm">* {t(errorMessage)}</p>}
            </div>
          )}
        />
        <WFPButton type="submit" disabled={isBusy}>
          {t("common.send")}
        </WFPButton>
      </form>
    </FormProvider>
  );
};
